import { Children } from 'react';
import { Badge } from '@/components/ui/badge';
import { FileAttachment, FileIconBox } from '@/components/ui/file-attachment';
import { CATEGORY_PILL_CLASSES } from '../../DataTable/constants';
import { EmptySkuIcon } from '../../icons';
import { StatusActiveIcon, StatusInactiveIcon } from '../../icons/StatusIcons';
import { ImageCarousel } from '../../common/ImageCarousel';
import { PdfFileIcon } from '../../icons/PdfFileIcon';
import { DietTagIcons } from '../DietTagIcons';
import type { Product } from '../types';
import { DEFAULT_FORM_LABELS, MACROS_FORMATS } from '../types';
import { useTranslation } from '@hooks/useTranslation';
import { formatCurrency, formatDateRange, formatPercent, formatTagParts } from '../../../utils/formatters';

interface ProductOverviewSectionProps {
  product: Product;
  showBcrsDeposit?: boolean;
  showDisplayCopy?: boolean;
}

function formatActivePeriod(start: string | null, end: string | null, always: string): string {
  if (start && end) return formatDateRange(start, end);
  if (start && !end) return always;
  return '\u2014';
}

function formatMacroValue(value: number | null | undefined, unit?: string): string {
  if (value == null) return '—';
  return unit ? `${value}${unit}` : `${value}`;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-inter text-sm font-semibold text-grey">
      {children}
    </span>
  );
}

function FieldValue({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-inter text-sm text-off-black mt-2">
      {children}
    </p>
  );
}

function FieldRow({ children }: { children: React.ReactNode }) {
  const columns = Children.toArray(children).length;
  return (
    <div className={columns > 1 ? 'grid grid-cols-2 gap-x-12' : 'grid grid-cols-1'}>
      {children}
    </div>
  );
}

function Divider() {
  return <div className="w-full border-t border-dinnerplate-white" />;
}

export function ProductOverviewSection({ product, showBcrsDeposit = false, showDisplayCopy = false }: ProductOverviewSectionProps) {
  const { t } = useTranslation();
  const showMacros = MACROS_FORMATS.includes(product.form);
  const grams = t('common.units.grams');
  const tagParts = formatTagParts(product.diet_tags, product.chef_recommended, product.nutrition_tags, product.allergen_tags, t('common.fields.chefs_pick'));
  const tagsDisplay = tagParts.length > 0 ? tagParts.join(', ') : '—';

  return (
    <div>
      <h3 className="font-kapra-expanded text-2xl font-black uppercase leading-tight text-off-black mb-4">
        {t('index.section.title')}
      </h3>

      <div className="flex min-w-fit gap-16 rounded-input border border-dinnerplate-white bg-off-white p-6">
        {/* Left column: image + display copy (mirrors the edit form's layout) */}
        <div className="flex w-preview-image shrink-0 flex-col gap-2 self-start">
          <ImageCarousel
            images={(product.images ?? []).map((img) => ({ url: img.url }))}
            placeholder={<EmptySkuIcon className="w-24 h-20 text-grey" />}
            className="w-full"
          />

          {/* Display copy (uploaded signage PDF) — sits under the product photo */}
          {showDisplayCopy && (
            <div className="mt-6">
              <FieldLabel>{t('common.fields.display_copy')}</FieldLabel>
              {product.display_attachment ? (
                <FileAttachment
                  className="mt-2"
                  leading={
                    <FileIconBox className="size-16">
                      <PdfFileIcon className="size-8 text-grey" />
                    </FileIconBox>
                  }
                  name={product.display_attachment.filename}
                  href={product.display_attachment.url}
                />
              ) : (
                <FieldValue>{t('common.fields.display_copy_empty')}</FieldValue>
              )}
            </div>
          )}
        </div>

        {/* Right column: ALL fields */}
        <div className="flex flex-1 flex-col gap-8">
          {/* Name + Chinese name */}
          <FieldRow>
            <div>
              <FieldLabel>{t('common.fields.name')}</FieldLabel>
              <p className="flex items-center gap-1 font-inter text-sm font-semibold text-off-black mt-2">
                {product.name}
                <DietTagIcons tags={product.diet_tags} chefRecommended={product.chef_recommended} />
              </p>
            </div>
            <div>
              <FieldLabel>{t('common.fields.chinese_name')}</FieldLabel>
              <FieldValue>{product.chinese_name || '—'}</FieldValue>
            </div>
          </FieldRow>

          {/* Category + Format */}
          <FieldRow>
            <div>
              <FieldLabel>{t('common.fields.category')}</FieldLabel>
              <div className="mt-2">
                {product.category ? (
                  <Badge variant="static" className={CATEGORY_PILL_CLASSES}>
                    {product.category}
                  </Badge>
                ) : (
                  <span className="font-inter text-sm text-grey">—</span>
                )}
              </div>
            </div>
            <div>
              <FieldLabel>{t('common.fields.format')}</FieldLabel>
              <FieldValue>{DEFAULT_FORM_LABELS[product.form]}</FieldValue>
            </div>
          </FieldRow>

          {/* Kitchen + Papercut category */}
          <FieldRow>
            <div>
              <FieldLabel>{t('common.fields.kitchen')}</FieldLabel>
              <FieldValue>
                {product.kitchen ? product.kitchen.charAt(0).toUpperCase() + product.kitchen.slice(1) : '—'}
              </FieldValue>
            </div>
            <div>
              <FieldLabel>{t('common.fields.papercut_category')}</FieldLabel>
              <FieldValue>{product.papercut_category || '—'}</FieldValue>
            </div>
          </FieldRow>

          {/* Kitchen section + BCRS deposit */}
          <FieldRow>
            <div>
              <FieldLabel>{t('common.fields.kitchen_section')}</FieldLabel>
              <FieldValue>
                {product.kitchen_section.length > 0 ? product.kitchen_section.join(', ') : '—'}
              </FieldValue>
            </div>
            {showBcrsDeposit && (
              <div>
                <FieldLabel>{t('common.fields.bcrs_deposit')}</FieldLabel>
                <div className="mt-2">
                  <Badge
                    variant="status"
                    tone={product.bcrs ? 'mint' : 'neutral'}
                    size="sm"
                    bordered
                    icon={product.bcrs ? <StatusActiveIcon /> : <StatusInactiveIcon />}
                  >
                    {product.bcrs ? t('common.fields.bcrs_enabled') : t('common.fields.bcrs_disabled')}
                  </Badge>
                </div>
              </div>
            )}
          </FieldRow>

          {/* Unit — own row */}
          <FieldRow>
            <div>
              <FieldLabel>{t('common.fields.display_unit')}</FieldLabel>
              <FieldValue>{product.display_unit || '—'}</FieldValue>
            </div>
            <div />
          </FieldRow>

          {/* Catalogue + Active period */}
          <FieldRow>
            <div>
              <FieldLabel>{t('common.fields.catalogue')}</FieldLabel>
              <div className="mt-2 flex flex-wrap gap-2">
                {product.catalogues.length > 0 ? (
                  product.catalogues.map((catalogue) => (
                    <Badge
                      key={catalogue}
                      variant="static"
                      className="font-inter w-fit rounded-xs border border-off-black bg-transparent text-off-black text-xs font-normal px-1.5 py-1 leading-tight-sm"
                    >
                      {catalogue}
                    </Badge>
                  ))
                ) : (
                  <span className="font-inter text-sm text-grey">—</span>
                )}
              </div>
            </div>
            <div>
              <FieldLabel>{t('common.fields.active_period')}</FieldLabel>
              <FieldValue>
                {formatActivePeriod(product.active_period_start, product.active_period_end, t('common.fields.active_period_always'))}
              </FieldValue>
            </div>
          </FieldRow>

          <Divider />

          {/* Before GST + After GST */}
          <FieldRow>
            <div>
              <FieldLabel>{t('common.fields.before_gst')}</FieldLabel>
              <FieldValue>
                {product.price_excluding_gst != null ? formatCurrency(product.price_excluding_gst) : '—'}
              </FieldValue>
            </div>
            <div>
              <FieldLabel>{t('common.fields.after_gst')}</FieldLabel>
              <FieldValue>
                {product.price_including_gst != null ? formatCurrency(product.price_including_gst) : '—'}
              </FieldValue>
            </div>
          </FieldRow>

          {/* Food cost ($) + Food cost (%) */}
          <FieldRow>
            <div>
              <FieldLabel>{t('common.fields.food_cost')}</FieldLabel>
              <FieldValue>
                {product.food_cost != null ? formatCurrency(product.food_cost) : '—'}
              </FieldValue>
            </div>
            <div>
              <FieldLabel>{t('common.fields.food_cost_percent')}</FieldLabel>
              <FieldValue>{formatPercent(product.food_cost_percent)}</FieldValue>
            </div>
          </FieldRow>

          <Divider />

          {/* Byline */}
          <div>
            <FieldLabel>{t('common.fields.byline')}</FieldLabel>
            <FieldValue>{product.byline || '—'}</FieldValue>
          </div>

          {/* Tags */}
          <div>
            <FieldLabel>{t('common.fields.tags')}</FieldLabel>
            <FieldValue>{tagsDisplay}</FieldValue>
          </div>

          {/* Ingredients */}
          <div>
            <FieldLabel>{t('common.fields.ingredients')}</FieldLabel>
            <FieldValue>{product.ingredients || '—'}</FieldValue>
          </div>

          {/* Macros (Meal Box / Treat Box only) */}
          {showMacros && (
            <>
              <Divider />

              <FieldRow>
                <div>
                  <FieldLabel>{t('common.fields.calories')}</FieldLabel>
                  <FieldValue>{formatMacroValue(product.macros?.calories)}</FieldValue>
                </div>
                <div>
                  <FieldLabel>{t('common.fields.protein')}</FieldLabel>
                  <FieldValue>{formatMacroValue(product.macros?.protein, grams)}</FieldValue>
                </div>
              </FieldRow>

              <FieldRow>
                <div>
                  <FieldLabel>{t('common.fields.fat')}</FieldLabel>
                  <FieldValue>{formatMacroValue(product.macros?.fats, grams)}</FieldValue>
                </div>
                <div>
                  <FieldLabel>{t('common.fields.carbs')}</FieldLabel>
                  <FieldValue>{formatMacroValue(product.macros?.carbohydrates, grams)}</FieldValue>
                </div>
              </FieldRow>
            </>
          )}

          <Divider />

          {/* Temperature + Serving size (serving size hidden for Meal Box / Treat Box) */}
          <FieldRow>
            <div>
              <FieldLabel>{t('common.fields.temperature')}</FieldLabel>
              <FieldValue>
                {product.serving_temperature
                  ? product.serving_temperature.charAt(0).toUpperCase() + product.serving_temperature.slice(1)
                  : '—'}
              </FieldValue>
            </div>
            {!showMacros && (
              <div>
                <FieldLabel>{t('common.fields.serving_size')}</FieldLabel>
                <FieldValue>{product.serving_size || '—'}</FieldValue>
              </div>
            )}
          </FieldRow>

          {/* Production notes */}
          <div>
            <FieldLabel>{t('common.fields.production_notes')}</FieldLabel>
            <FieldValue>{product.production_notes || '—'}</FieldValue>
          </div>

          {/* Fulfilment notes */}
          <div>
            <FieldLabel>{t('common.fields.fulfilment_notes')}</FieldLabel>
            <FieldValue>{product.fulfilment_notes || '—'}</FieldValue>
          </div>

          {/* Key serving info */}
          <div>
            <FieldLabel>{t('common.fields.key_serving_info')}</FieldLabel>
            <FieldValue>{product.key_serving_info || '—'}</FieldValue>
          </div>

          {/* Story */}
          <div>
            <FieldLabel>{t('common.fields.story')}</FieldLabel>
            <FieldValue>{product.story || '—'}</FieldValue>
          </div>
        </div>
      </div>
    </div>
  );
}
